import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { rgba } from 'polished';

import { OptionsRouteParams } from '#/routes';
import {
  useOptionTableSettings,
  useOptionTablesView,
} from '#/features/options/option-tables-context';
import { useOrderModalActions } from '#/features/options/order-modal/order-modal-context';
import { Error } from '#/components/options/atoms';
import { c13132E, mainGridGapColor, c7239C4 } from '#/colors';

import { BaseRow, BaseRowGroup } from './atoms';
import TableTitle from './TableTitle';
import HeaderRow from './HeaderRow';
import Cell, { CellStrike } from './Cell';

interface Props {
  readonly className?: string;
}

export default function OptionTables({ className }: Props) {
  const routeParams = useParams<OptionsRouteParams>();
  const optionTablesView = useOptionTablesView();
  const optionTableSettings = useOptionTableSettings();
  // WIP: this logic is to be extracted into a context, also adding support for 'ALL' expiry date.
  const optionTableView =
    routeParams.expiryDate != null
      ? optionTablesView.tables[routeParams.expiryDate]
      : null;
  const orderModalActions = useOrderModalActions();

  if (optionTablesView.status === 'loading') {
    return null;
  }

  if (optionTableView == null) {
    return (
      <Error>{`No data for Option '${routeParams.asset} ${routeParams.expiryDate}'`}</Error>
    );
  }

  const openOptionModal = (symbol: string) => {
    orderModalActions.open(symbol);
  };

  return (
    <Container className={className}>
      <TableTitle optionTableView={optionTableView} />
      <Table>
        <HeaderRow />
        {optionTableView.rows.map((option) => (
          <Row key={option.strikePriceQuoteCurrency}>
            {/* CALL */}
            <RowGroup onClick={() => openOptionModal(option.CALL.symbol)}>
              {optionTableSettings.columns.map((settings) => (
                <Cell
                  key={settings.key}
                  settings={settings}
                  view={option.CALL}
                />
              ))}
            </RowGroup>

            {/* STRIKE */}
            <CellStrike>{option.strikePriceQuoteCurrency}</CellStrike>

            {/* PUT */}
            <RowGroup onClick={() => openOptionModal(option.PUT.symbol)}>
              {optionTableSettings.columns.map((settings) => (
                <Cell
                  key={settings.key}
                  settings={settings}
                  view={option.PUT}
                />
              ))}
            </RowGroup>
          </Row>
        ))}
      </Table>
    </Container>
  );
}

const Container = styled.div``;

const Table = styled.div``;

const Row = styled(BaseRow)`
  background-color: ${c13132E};
  &:nth-child(odd) {
    background-color: ${rgba(mainGridGapColor, 0.5)};
  }
`;

const RowGroup = styled(BaseRowGroup)`
  cursor: pointer;
  &:hover {
    background-color: ${rgba(c7239C4, 0.5)};
  }
`;
